import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../components/ui/Button.jsx';
import { Modal } from '../components/ui/Modal.jsx';
import { useTranslation } from '../i18n/useTranslation.js';

import { ProvisioningStatusPanel } from './ProvisioningStatusPanel.jsx';
import { tenantsApi } from './platformApi.js';

/**
 * Schlägt ein Kürzel aus dem Firmennamen vor.
 *
 * Exportiert, damit es geprüft werden kann: das Kürzel bildet acht
 * Datenbanknamen und einen Rollennamen, und tenants_slug_check lässt nur
 * ASCII zu. Eine stille Änderung hier wäre teuer.
 */
export function suggestSlug(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[äàáâ]/g, 'a')
    .replace(/[öòóô]/g, 'o')
    .replace(/[üùúû]/g, 'u')
    .replace(/[éèêë]/g, 'e')
    /* Das scharfe S wird zu ss.
     *
     * Hier stand zwischenzeitlich .replace(/ss/g, 'ss') - eine Regel, die
     * "ss" durch "ss" ersetzt und damit nichts tut. Ein Durchgang, der im
     * ganzen Projekt ß durch ss ersetzte (Schweizer Schreibweise), hatte
     * auch das SUCHMUSTER erwischt. Folge: aus "Straßenbau AG" wurde
     * stra_enbau_ag statt strassenbau_ag - kein Absturz, nur ein
     * dauerhaft hässlicher Datenbankname. */
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^([0-9])/, 'f$1')
    .slice(0, 24);
}

const EMPTY = {
  name: '',
  slug: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  adminEmail: '',
  adminPassword: '',
  adminFirstName: '',
  adminLastName: '',
  addressLine1: '',
  zip: '',
  city: '',
  companyEmail: '',
  phone: '',
  iban: '',
  vatNumber: '',
};

/**
 * Legt eine Firma an und richtet sie ein.
 *
 * Das Fenster geht nach dem Absenden NICHT zu. Der Anbieter hat gerade eine
 * Kundenfirma bestellt; ihn danach mit einer Liste alleinzulassen, in der eine
 * Zeile "in Einrichtung" steht, wäre die schlechtere Auskunft. Stattdessen
 * laufen die Schritte sichtbar durch, und am Ende steht die Anmeldeadresse da,
 * die der Kunde braucht.
 */
export function NewTenantModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [error, setError] = useState(null);
  const [fields, setFields] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [finishedJob, setFinishedJob] = useState(null);

  function reset() {
    setForm(EMPTY);
    setSlugTouched(false);
    setError(null);
    setFields(null);
    setJobId(null);
    setFinishedJob(null);
  }

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const mutation = useMutation({
    mutationFn: () =>
      tenantsApi.create({
        name: form.name.trim(),
        slug: form.slug.trim(),
        contactName: form.contactName.trim(),
        contactEmail: form.contactEmail.trim(),
        contactPhone: form.contactPhone.trim(),
        admin: {
          email: form.adminEmail.trim(),
          password: form.adminPassword,
          firstName: form.adminFirstName.trim(),
          lastName: form.adminLastName.trim(),
        },
        company: {
          addressLine1: form.addressLine1.trim(),
          zip: form.zip.trim(),
          city: form.city.trim(),
          email: form.companyEmail.trim(),
          phone: form.phone.trim(),
          iban: form.iban.replace(/\s+/g, ''),
          vatNumber: form.vatNumber.trim(),
        },
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      setError(null);
      setFields(null);
      setJobId(data?.job?.id || null);
    },
    onError: (err) => {
      setError(err?.message || t('platform.tenants.create.failed'));
      setFields(err?.fields || null);
    },
  });

  function setName(value) {
    setForm((prev) => ({
      ...prev,
      name: value,
      // Solange niemand das Kürzel angefasst hat, läuft es dem Namen nach.
      slug: slugTouched ? prev.slug : suggestSlug(value),
    }));
  }

  function handleClose() {
    // Die Liste kann sich während der Einrichtung geändert haben.
    queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
    reset();
    onClose();
  }

  const canSubmit =
    form.name.trim() && form.slug.trim() && form.adminEmail.trim() && form.adminPassword.length >= 10;

  const running = Boolean(jobId);

  return (
    <Modal
      open={open}
      title={running ? t('platform.provisioning.title') : t('platform.tenants.create.title')}
      onClose={handleClose}
      footer={
        running ? (
          <Button variant="primary" onClick={handleClose} disabled={!finishedJob}>
            {finishedJob ? t('common.close') : t('platform.provisioning.pleaseWait')}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={handleClose}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || !canSubmit}
            >
              {mutation.isPending ? t('platform.tenants.create.saving') : t('platform.tenants.create.cta')}
            </Button>
          </>
        )
      }
    >
      {running ? (
        <>
          <ProvisioningStatusPanel jobId={jobId} onFinished={setFinishedJob} />
          {finishedJob?.status === 'done' && (
            <div className="alert success text-sm">
              {t('platform.provisioning.readyHint', { email: form.adminEmail.trim() })}
            </div>
          )}
        </>
      ) : (
        <>
          <p className="muted text-sm" style={{ marginTop: 0 }}>
            {t('platform.tenants.create.hint')}
          </p>

          <h4 className="form-section-title">{t('platform.tenants.section.company')}</h4>

          <label className="field">
            <span className="field-label">{t('platform.tenants.col.name')}</span>
            <input className="input" value={form.name} onChange={(e) => setName(e.target.value)} autoFocus required />
          </label>

          <label className="field">
            <span className="field-label">{t('platform.tenants.col.slug')}</span>
            <input
              className="input mono"
              value={form.slug}
              onChange={(e) => {
                setSlugTouched(true);
                setForm((prev) => ({ ...prev, slug: e.target.value }));
              }}
              required
            />
            <span className="muted text-xs">{t('platform.tenants.slug.hint')}</span>
          </label>

          <label className="field">
            <span className="field-label">{t('platform.tenants.col.contact')}</span>
            <input className="input" value={form.contactName} onChange={set('contactName')} />
          </label>

          <label className="field">
            <span className="field-label">{t('common.email')}</span>
            <input className="input" type="email" value={form.contactEmail} onChange={set('contactEmail')} />
          </label>

          <label className="field">
            <span className="field-label">{t('platform.tenants.col.phone')}</span>
            <input className="input" value={form.contactPhone} onChange={set('contactPhone')} />
          </label>

          <h4 className="form-section-title">{t('platform.tenants.section.admin')}</h4>
          <p className="muted text-xs" style={{ marginTop: 0 }}>
            {t('platform.tenants.section.admin.hint')}
          </p>

          <div className="field-row">
            <label className="field">
              <span className="field-label">{t('platform.tenants.admin.firstName')}</span>
              <input className="input" value={form.adminFirstName} onChange={set('adminFirstName')} />
            </label>
            <label className="field">
              <span className="field-label">{t('platform.tenants.admin.lastName')}</span>
              <input className="input" value={form.adminLastName} onChange={set('adminLastName')} />
            </label>
          </div>

          <label className="field">
            <span className="field-label">{t('platform.tenants.admin.email')}</span>
            <input className="input" type="email" value={form.adminEmail} onChange={set('adminEmail')} required />
          </label>

          <label className="field">
            <span className="field-label">{t('platform.tenants.admin.password')}</span>
            <input
              className="input"
              type="password"
              value={form.adminPassword}
              onChange={set('adminPassword')}
              minLength={10}
              required
            />
            <span className="muted text-xs">{t('platform.tenants.admin.password.hint')}</span>
          </label>

          <h4 className="form-section-title">{t('platform.tenants.section.letterhead')}</h4>
          <p className="muted text-xs" style={{ marginTop: 0 }}>
            {t('platform.tenants.section.letterhead.hint')}
          </p>

          <label className="field">
            <span className="field-label">{t('platform.tenants.company.address')}</span>
            <input className="input" value={form.addressLine1} onChange={set('addressLine1')} />
          </label>

          <div className="field-row">
            <label className="field" style={{ maxWidth: '8rem' }}>
              <span className="field-label">{t('platform.tenants.company.zip')}</span>
              <input className="input" value={form.zip} onChange={set('zip')} />
            </label>
            <label className="field">
              <span className="field-label">{t('platform.tenants.company.city')}</span>
              <input className="input" value={form.city} onChange={set('city')} />
            </label>
          </div>

          <label className="field">
            <span className="field-label">{t('platform.tenants.company.iban')}</span>
            <input className="input mono" value={form.iban} onChange={set('iban')} />
          </label>

          <label className="field">
            <span className="field-label">{t('platform.tenants.company.vat')}</span>
            <input className="input mono" value={form.vatNumber} onChange={set('vatNumber')} />
          </label>

          {error && (
            <div className="alert danger">
              {error}
              {fields && (
                <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: '1.2em' }}>
                  {fields.map((f) => (
                    <li key={f.path}>{f.message}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
